import './context-dropdown-list.scss';

import { groupBy, isEmpty, map } from 'lodash';
import {
  Fragment,
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import { ContextDropdownListState } from '../../data/utils/use-context-dropdown-list';

const MARGIN = 12;

export type ContextDropdownListItem = {
  value: string;
  title: string;
  disabled?: boolean;
  category?: string;
};

type ContextDropdownListProps = {
  items: (ContextDropdownListItem | undefined | false)[];
  state: ContextDropdownListState | false;
  className?: string;
  grouped?: boolean;
  onSelect?: (value: ContextDropdownListItem) => void;
};

export function ContextDropdownList({
  items,
  state,
  className,
  grouped = true,
  onSelect,
}: ContextDropdownListProps) {
  const container = document.getElementById('portal-container');

  const [style, setStyle] = useState<{
    top: string;
    left: string;
    width: string;
    opacity: number;
  }>();

  useEffect(() => {
    if (state) {
      const refElement = (state.ref as RefObject<HTMLDivElement>)?.current;

      if (refElement) {
        const element = refElement.getBoundingClientRect();

        setStyle({
          opacity: 1,
          top: `${Math.max(
            Math.min(
              state.y + state.height,
              window.innerHeight - element.height - MARGIN
            ),
            MARGIN
          )}px`,
          left: `${state.x}px`,
          width: `${state.width}px`,
        });
      }
    }

    return () => {
      setStyle(undefined);
    };
  }, [state, items]);

  const onHandleItem = useCallback(
    (e: MouseEvent<HTMLElement>, value: ContextDropdownListItem) => {
      e.preventDefault();
      e.stopPropagation();

      onSelect?.(value);
    },
    [onSelect]
  );

  const formattedItems = useMemo(
    () =>
      groupBy(
        items
          .filter(option => option)
          .map(option => option as ContextDropdownListItem),
        option => option.category ?? ''
      ),
    [items]
  );

  if (!container || !state || isEmpty(formattedItems)) return null;

  return ReactDOM.createPortal(
    <div
      className={`context-dropdown-list${
        className ? ` context-dropdown-list--${className}` : ``
      }`}
      style={style}
      ref={state.ref}
    >
      <div className="context-dropdown-list__content">
        {map(formattedItems, (options, key) => (
          <Fragment key={key}>
            {grouped && <h2>{key}</h2>}
            <ul>
              {options.map(option => (
                <li key={option.value}>
                  <button
                    type="button"
                    disabled={option.disabled ?? !onSelect}
                    onClick={e => onHandleItem(e, option)}
                  >
                    <span>{option.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Fragment>
        ))}
      </div>
    </div>,
    container
  );
}
